import {
  Mail,
  Phone,
  MapPin,
  Github,
  Linkedin,
  Twitter,
  Instagram,
  ArrowRight,
  Zap
} from 'lucide-react';

const ModernFooter = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    society: [
      { name: 'About Us', href: '/about' },
      { name: 'Our Team', href: '/team' },
      { name: 'Mission & Vision', href: '/mission' },
      { name: 'History', href: '/history' }
    ],
    programs: [
      { name: 'Research Projects', href: '/projects' },
      { name: 'Workshops', href: '/workshops' },
      { name: 'Competitions', href: '/competitions' },
      { name: 'Internships', href: '/internships' }
    ],
    resources: [
      { name: 'Documentation', href: '/docs' },
      { name: 'Publications', href: '/publications' },
      { name: 'Gallery', href: '/gallery' },
    ],
    connect: [
      { name: 'Events', href: '/events' },
      { name: 'Join Us', href: '/join' },
      { name: 'Contact', href: '/contact' },
    ]
  };

  const socialLinks = [
    { icon: Github, href: 'https://github.com/Aeronauts-nsut', label: 'GitHub' },
    { icon: Linkedin, href: 'https://www.linkedin.com/company/aeronauts-nsut/', label: 'LinkedIn' },
    { icon: Instagram, href: 'https://www.instagram.com/aeronauts.nsut/', label: 'Instagram' }
  ];

  const contactInfo = [
    { icon: Mail, text: 'aeronauts.nsut@gmail.com', href: 'mailto:aeronauts.nsut@gmail.com' },
    { icon: MapPin, text: 'Netaji Subhas University, Delhi', href: 'https://www.nsut.ac.in/' }
  ];

  return (
    <footer className="relative bg-slate-950 border-t border-white/10">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-drone-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Newsletter Section */}

        {/* Main Footer Content */}
        <div className="py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
            {/* Brand Section */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">

                <span className="brand-text text-3xl font-bold">aeronauts</span>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Pioneering the future of aerial robotics through cutting-edge research,
                innovation, and collaborative excellence in drone technology.
              </p>

              {/* Contact Info */}
              <div className="space-y-3">
                {contactInfo.map((contact, index) => (
                  <a
                    key={index}
                    href={contact.href}
                    className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors group"
                  >
                    <contact.icon className="h-4 w-4 text-blue-400 group-hover:text-blue-300" />
                    <span className="text-sm">{contact.text}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Footer Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Society</h4>
              <ul className="space-y-2">
                {footerLinks.society.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className="text-gray-300 hover:text-white transition-colors text-sm"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Programs</h4>
              <ul className="space-y-2">
                {footerLinks.programs.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className="text-gray-300 hover:text-white transition-colors text-sm"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Resources</h4>
              <ul className="space-y-2">
                {footerLinks.resources.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className="text-gray-300 hover:text-white transition-colors text-sm"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Connect</h4>
              <ul className="space-y-2 mb-6">
                {footerLinks.connect.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className="text-gray-300 hover:text-white transition-colors text-sm"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>

              {/* Social Links */}
              <div>
                <h5 className="text-white font-medium mb-3 text-sm">Follow Us</h5>
                <div className="flex gap-3">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-slate-800 text-gray-400 hover:bg-blue-600 hover:text-white transition-all"
                      aria-label={social.label}
                    >
                      <social.icon className="h-4 w-4" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-white/10">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-400">
              © {currentYear} aeronauts. All rights reserved.
            </div>
            <div className="flex gap-6 text-sm">
              <a href="/privacy" className="text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="/terms" className="text-gray-400 hover:text-white transition-colors">
                Terms of Service
              </a>

            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ModernFooter;